import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { toast } from "sonner";
import { useUploadMediaImageMutation } from "@/app/features/mediaGallery/mediaGalleryApiSlice";

const MEDIA_BATCH_UPLOAD_SIZE = 10;

const baseStyle = {
  flex: 1,
  display: "flex",
  FlexDirection: "column",
  alignItems: "center",
  padding: "20px",
  borderWidth: 2,
  borderRadius: 2,
  borderColor: "#eeeeee",
  borderStyle: "dashed",
  backgroundColor: "#fafafa",
  color: "#bdbdbd",
  outline: "none",
  transition: "border .24s ease-in-out",
};

const focusedStyle = {
  borderColor: "#2196f3",
};

const acceptStyle = {
  borderColor: "#00e676",
};

const rejectStyle = {
  borderColor: "#ff1744",
};

const UploadMediaPage = () => {
  const [files, setFiles] = useState([]);

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        py: 8,
      }}
    >
      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Stack direction="row" justifyContent="space-between" spacing={4}>
            <Typography variant="h4">Upload Media Images</Typography>
          </Stack>
          {files.length > 0 ? (
            <PreviewFiles files={files} setFiles={setFiles} />
          ) : (
            <DragAndDrop setFiles={setFiles} />
          )}
        </Stack>
      </Container>
    </Box>
  );
};

function DragAndDrop({ setFiles }) {
  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({
      accept: { "image/webp": [] },
      onDrop: (acceptedFiles) => {
        setFiles(acceptedFiles);
      },
      onDropRejected: (fileRejections) => {
        console.log(fileRejections);
        fileRejections.forEach((fileRejection) => {
          toast.error(fileRejection.errors[0].message);
        });
      },
    });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  return (
    <div className="container">
      <div {...getRootProps({ style })}>
        <input {...getInputProps()} />
        <Stack
          alignItems="center"
          justifyContent="center"
          margin="0 auto"
          gap={1}
        >
          <FileUploadIcon />
          Drag 'n' drop some files here, or click to select files
        </Stack>
      </div>
      <p
        style={{ color: "#A0AEC0", marginTop: "0.5rem", marginLeft: "0.5rem" }}
      >
        Note: Only WEBP file format supported
      </p>
    </div>
  );
}

function PreviewFiles({ files, setFiles }) {
  const removeFile = (file) => {
    setFiles(files.filter((f) => f.name !== file.name));
  };

  const [isImagesLoading, setIsImagesLoading] = useState(false);

  const [uploadMediaImage] = useUploadMediaImageMutation();

  function chunkArray(inputArray, chunkSize) {
    if (chunkSize <= 0) {
      throw new Error("Chunk size must be greater than 0");
    }

    const result = [];
    for (let i = 0; i < inputArray.length; i += chunkSize) {
      result.push(inputArray.slice(i, i + chunkSize));
    }

    return result;
  }

  async function batchUploadMedia(files) {
    const formData = new FormData();

    files.forEach((file) => {
      formData.append("images", file);
    });

    return await uploadMediaImage(formData);
  }

  async function uploadMedia(files) {
    const result = chunkArray(files, MEDIA_BATCH_UPLOAD_SIZE);

    const promises = result.map(async (chunk, index) => {
      await batchUploadMedia(chunk);
      console.log(index);
    });

    await Promise.all(promises);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    // setIsImagesLoading(true);

    toast.promise(uploadMedia(files), {
      loading: "Uploading files please do not refresh the page",
      success: () => {
        setIsImagesLoading(false);
        setFiles([]);
        return `All images have been uploaded`;
      },
      error: () => {
        setIsImagesLoading(false);
        return "Some error has occured while uploading the images";
      },
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid spacing={2} mt={2} container>
        {files.map((file) => (
          <Grid
            item
            xs={12}
            sm={4}
            md={3}
            lg={2}
            sx={{
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundSize: "contain",
              borderRadius: "0.375rem",
              overflow: "auto",
              padding: "1rem",
              transition: "all 0.2s",
              width: "100%",
              marginTop: "0",
              marginBottom: "0",
              backgroundColor: "transparent",
              position: "relative",
            }}
            key={file.name}
          >
            <ImageView file={file} removeFile={removeFile} />
          </Grid>
        ))}
      </Grid>

      <Button
        type="submit"
        variant="contained"
        fullWidth
        sx={{ mt: 1 }}
        disabled={isImagesLoading}
      >
        Upload
      </Button>
    </form>
  );
}

function ImageView({ file, removeFile }) {
  const preview = URL.createObjectURL(file);
  return (
    <>
      <img
        src={preview}
        style={{
          objectFit: "cover",
          boxShadow:
            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          borderRadius: "0.375rem",
          width: "100%",
          maxHeight: "48rem",
        }}
        onLoad={() => {
          URL.revokeObjectURL(preview);
        }}
        alt={file.name}
      />
      <IconButton
        type="button"
        style={{
          backgroundColor: "rgb(234, 234, 236)",
          position: "absolute",
          top: "0",
          left: "0",
          padding: "0.1rem",
        }}
        title="Clear Image"
        onClick={() => removeFile(file)}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            width: "12px",
            height: "12px",
          }}
        >
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </IconButton>
    </>
  );
}

export default UploadMediaPage;
